import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import api from "../utils/api";
import { toast } from "sonner";

interface User {
  id: string;
  email: string;
  name?: string;
  onboardingStatus: "pending" | "completed";
  role: string;
  plan?: "free" | "premium" | "custom" | "growth";
  createdAt?: string;
  subscription?: {
    lastInterval?: string;
    premiumExpiresAt?: string;
    lastPaymentReference?: string;
    paystackCustomerCode?: string;
  } | null;
  storePermissions?: {
    storeId: string;
    storeName: string;
    permissions: string[];
    owner: string;
  };
}

interface Store {
  _id: string;
  storeName: string;
  url?: string;
  logo?: string;
  currency?: string; // Store currency for formatting
  isOwner: boolean; // true if user is the store owner, false if manager
  permissions?: string[]; // permissions for this store if user is a manager
  managers?: string[];
  owner?: string; // ID of the store owner
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  stores: Store[];
  currentStore: Store | null;
  isLoading: boolean;
  isLoadingStores: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  requestMagicLink: (email: string) => Promise<boolean>;
  verifyMagicLink: (email: string, code: string) => Promise<void>;
  logout: () => void;
  fetchUserStores: () => Promise<void>;
  refreshUser: () => Promise<void>;
  switchStore: (storeId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [currentStore, setCurrentStore] = useState<Store | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const fetchUserStores = useCallback(async () => {
    setIsLoadingStores(true);
    try {
      const { data } = await api.get("/api/stores/user");
      setStores(data);

      // Don't set currentStore here - let the useEffect handle it
      // This prevents conflicts between fetchUserStores and useEffect
    } catch (error) {
      console.error("Error fetching user stores:", error);
      // On error, set empty stores array
      setStores([]);
    } finally {
      setIsLoadingStores(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/api/auth/me");
      setUser(data);

      // Also refresh stores to ensure everything is up to date
      await fetchUserStores();
    } catch (error) {
      console.error("Error refreshing user data:", error);
      throw error;
    }
  }, [fetchUserStores]);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem("sq_token");
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        const { data } = await api.get("/api/auth/me");

        // Set authentication state
        setUser(data);
        setIsAuthenticated(true);

        // For all authenticated users, fetch their stores
        // The useEffect will handle setting the correct currentStore
        if (data) {
          await fetchUserStores();

          // If user is a manager and we received store permissions,
          // add it to stores array to ensure it's available
          if (data.role === "manager" && data.storePermissions) {
            const storeData = {
              _id: data.storePermissions.storeId,
              storeName: data.storePermissions.storeName,
              isOwner: false,
              permissions: data.storePermissions.permissions,
              owner: data.storePermissions.owner,
              managers: [data.id], // Add current user as manager
            };

            // Add to stores if not already there (in case fetchUserStores didn't include it)
            setStores((prevStores) => {
              const existingStore = prevStores.find(
                (store) => store._id === storeData._id
              );
              if (existingStore) {
                return prevStores; // Already exists
              }
              return [...prevStores, storeData];
            });
          }
        }
      } catch (error) {
        // If there's an error, clear the invalid token
        console.error("Auth initialization error:", error);
        localStorage.removeItem("sq_token");
        api.defaults.headers.common["Authorization"] = "";
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [fetchUserStores]);

  const switchStore = async (storeId: string) => {
    const store = stores.find((s) => s._id === storeId);
    if (store) {
      // Set in localStorage first
      localStorage.setItem("sq_current_store", storeId);

      // Then update current store with owner status
      const updatedStore = {
        ...store,
        isOwner: store.isOwner || (user?.id && store.owner === user.id),
      };
      setCurrentStore(updatedStore);

      // Update API header with new store ID
      api.defaults.headers.common["Store-ID"] = storeId;

      // Notify user of the store switch
      toast.success(`Switched to ${store.storeName}`);

      // Trigger a page reload to refresh all data
      // This ensures all components update with the new store context
      window.location.reload();
    }
  };

  const login = async (email: string, password: string) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    localStorage.setItem("sq_token", data.token);
    api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    setUser(data.user);

    // After login, fetch user's stores
    await fetchUserStores();
  };

  const register = async (data: RegisterData) => {
    const response = await api.post("/api/auth/register", data);
    localStorage.setItem("sq_token", response.data.token);
    api.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${response.data.token}`;
    setUser(response.data.user);
  };

  const requestMagicLink = async (email: string): Promise<boolean> => {
    const { data } = await api.post("/api/auth/magic-link", { email });
    return data.isNewUser;
  };

  const verifyMagicLink = async (
    email: string,
    code: string,
    businessData?: any
  ) => {
    try {
      const response = await api.post("/api/auth/verify", {
        email,
        code,
        businessData: businessData || {},
      });

      // Set user data in state
      setUser(response.data.user);
      setIsAuthenticated(true);

      // Save token to localStorage for persistence
      localStorage.setItem("sq_token", response.data.token);
      api.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${response.data.token}`;

      // Load user's stores
      await fetchUserStores();

      // Return the full response for LoginPage to check onboarding status
      return response.data;
    } catch (error) {
      console.error("Error verifying magic link:", error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem("sq_token");
    localStorage.removeItem("sq_current_store");
    api.defaults.headers.common["Authorization"] = "";
    api.defaults.headers.common["Store-ID"] = "";
    setUser(null);
    setStores([]);
    setCurrentStore(null);
  };

  // Set current store when user and stores are available
  useEffect(() => {
    // Only proceed if user is authenticated and not loading stores
    if (!isAuthenticated || !user || isLoadingStores) {
      return;
    }

    // If user has no stores, ensure currentStore is null
    if (stores.length === 0) {
      setCurrentStore(null);
      // Clear any stored store ID and API headers
      localStorage.removeItem("sq_current_store");
      delete api.defaults.headers.common["Store-ID"];
      return;
    }

    // If user already has a currentStore and it's valid, don't change it
    if (
      currentStore &&
      stores.find((store) => store._id === currentStore._id)
    ) {
      return;
    }

    // Determine which store to set as current
    let storeToSet = null;

    // For managers, find their managed store
    if (user.role === "manager") {
      storeToSet = stores.find(
        (store) =>
          store.managers?.includes(user.id) ||
          (store.isOwner === false && store.permissions) // Store from storePermissions
      );
    }

    // For owners or if no managed store found, use stored preference or first store
    if (!storeToSet) {
      const storedStoreId = localStorage.getItem("sq_current_store");
      storeToSet = storedStoreId
        ? stores.find((store) => store._id === storedStoreId)
        : stores[0];
    }

    // Set the current store if found
    if (storeToSet) {

      // Save to localStorage
      localStorage.setItem("sq_current_store", storeToSet._id);

      // Determine if user is owner
      const isOwner =
        storeToSet.isOwner === true ||
        (user.id && storeToSet.owner === user.id);

      setCurrentStore({
        ...storeToSet,
        isOwner,
      });

      // Set the store ID in API headers for future requests
      api.defaults.headers.common["Store-ID"] = storeToSet._id;
    } else {
      setCurrentStore(null);
      localStorage.removeItem("sq_current_store");
      delete api.defaults.headers.common["Store-ID"];
    }
  }, [isAuthenticated, user, stores, isLoadingStores]);

  return (
    <AuthContext.Provider
      value={{
        user,
        stores,
        currentStore,
        isLoading,
        isLoadingStores,
        isAuthenticated,
        login,
        register,
        requestMagicLink,
        verifyMagicLink,
        logout,
        fetchUserStores,
        refreshUser,
        switchStore,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
